import { useMemo, useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight, Calculator } from "lucide-react";
import { EXTRAS, GIFTS, MENU } from "@/data/content";
import { prefillEnquiry } from "@/lib/scroll";
import { SectionHeading } from "./PriceMenu";
import { RevolutDepositButton } from "./RevolutDepositButton";

const euro = (str) =>
  Number((String(str).match(/\d+(?:\.\d+)?/) || [0])[0]) || 0;
const fmt = (n) => `€${n.toFixed(n % 1 ? 2 : 0)}`;

const Field = ({ label, children }) => (
  <label className="block">
    <span className="mb-2 block text-[10px] uppercase tracking-[0.28em] text-[#9A7B24]">
      {label}
    </span>
    {children}
  </label>
);

const selectCls =
  "w-full rounded-sm border border-[#D4AF37]/30 bg-white px-4 py-3 text-sm text-[#1B3A6B] focus:border-[#D4AF37] focus:outline-none";

export const PriceCalculator = () => {
  const [catId, setCatId] = useState(MENU[0].id);
  const [tierIdx, setTierIdx] = useState(0);
  const [flavor, setFlavor] = useState(MENU[0].flavors[0]);
  const [filling, setFilling] = useState(MENU[0].fillings[0]);
  const [style, setStyle] = useState("");
  const [addons, setAddons] = useState({});
  const [extras, setExtras] = useState({});

  const cat = MENU.find((m) => m.id === catId) || MENU[0];
  const tier = cat.tiers[Math.min(tierIdx, cat.tiers.length - 1)];

  const changeCategory = (id) => {
    const next = MENU.find((m) => m.id === id);
    setCatId(id);
    setTierIdx(0);
    setStyle("");
    if (!next.flavors.includes(flavor)) setFlavor(next.flavors[0]);
    if (!next.fillings.includes(filling)) setFilling(next.fillings[0]);
  };

  const chosenGifts = GIFTS.filter((g) => addons[g.id]);
  const chosenExtras = EXTRAS.filter((x) => extras[x.id]);

  const { cakePrice, addonTotal, total } = useMemo(() => {
    const cakePrice = euro(tier.price);
    const addonTotal =
      chosenGifts.reduce((sum, g) => sum + euro(g.price), 0) +
      chosenExtras.reduce((sum, x) => sum + euro(x.price), 0);
    return { cakePrice, addonTotal, total: cakePrice + addonTotal };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tier, addons, extras]);

  const summary = () => {
    const boxes = chosenGifts.map((g) => `${g.name} (${g.price})`);
    const touches = chosenExtras.map((x) => `${x.name} (${x.price})`);
    return [
      `Cake idea from the price calculator:`,
      `• ${cat.name} — ${tier.name} (${tier.serves}) — ${tier.price}`,
      style ? `• Style: ${style}` : null,
      `• Flavour: ${flavor} · Filling: ${filling}`,
      boxes.length ? `• Treat boxes: ${boxes.join(", ")}` : null,
      touches.length ? `• Finishing touches: ${touches.join(", ")}` : null,
      `• Estimated total: ${fmt(total)} (50% deposit ≈ ${fmt(total / 2)})`,
    ]
      .filter(Boolean)
      .join("\n");
  };

  return (
    <section
      id="calculator"
      data-testid="calculator-section"
      className="border-y border-[#D4AF37]/10 bg-[#F7F2E8]/60 py-24 lg:py-32"
    >
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <SectionHeading
          eyebrow="Plan Your Budget"
          title="Price Calculator"
          testId="calculator-heading"
        />
        <p className="mx-auto mt-5 max-w-xl text-center text-sm leading-relaxed text-[#67718A]">
          Build your cake and see an instant price guide. Final prices are
          confirmed in your quote and may vary with design detail.
        </p>

        <motion.div
          initial={{ opacity: 0, y: 28 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.6 }}
          className="mt-14 grid gap-8 lg:grid-cols-5"
        >
          <div className="space-y-6 rounded-sm border border-[#D4AF37]/20 bg-white/70 p-8 backdrop-blur-xl lg:col-span-3">
            <div className="grid gap-6 sm:grid-cols-2">
              <Field label="Collection">
                <select
                  data-testid="calc-category-select"
                  className={selectCls}
                  value={catId}
                  onChange={(e) => changeCategory(e.target.value)}
                >
                  {MENU.map((m) => (
                    <option key={m.id} value={m.id}>
                      {m.name}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Size">
                <select
                  data-testid="calc-size-select"
                  className={selectCls}
                  value={tierIdx}
                  onChange={(e) => setTierIdx(Number(e.target.value))}
                >
                  {cat.tiers.map((t, i) => (
                    <option key={t.name} value={i}>
                      {t.name} · {t.serves}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Flavour">
                <select
                  data-testid="calc-flavor-select"
                  className={selectCls}
                  value={flavor}
                  onChange={(e) => setFlavor(e.target.value)}
                >
                  {cat.flavors.map((f) => (
                    <option key={f}>{f}</option>
                  ))}
                </select>
              </Field>
              <Field label="Filling">
                <select
                  data-testid="calc-filling-select"
                  className={selectCls}
                  value={filling}
                  onChange={(e) => setFilling(e.target.value)}
                >
                  {cat.fillings.map((f) => (
                    <option key={f}>{f}</option>
                  ))}
                </select>
              </Field>
              {cat.styles?.length > 0 && (
                <Field label="Style (optional)">
                  <select
                    data-testid="calc-style-select"
                    className={selectCls}
                    value={style}
                    onChange={(e) => setStyle(e.target.value)}
                  >
                    <option value="">Choose a style…</option>
                    {cat.styles.map((s) => (
                      <option key={s.name}>{s.name}</option>
                    ))}
                  </select>
                </Field>
              )}
            </div>

            <div>
              <p className="mb-3 text-[10px] uppercase tracking-[0.28em] text-[#9A7B24]">
                Add treat boxes (optional)
              </p>
              <div className="grid gap-2 sm:grid-cols-2">
                {GIFTS.map((g) => (
                  <label
                    key={g.id}
                    data-testid={`calc-addon-${g.id}`}
                    className={`flex cursor-pointer items-center justify-between gap-3 rounded-sm border px-4 py-3 text-sm transition-colors ${
                      addons[g.id]
                        ? "border-[#D4AF37] bg-[#D4AF37]/10 text-[#1B3A6B]"
                        : "border-[#D4AF37]/25 bg-white text-[#2F4A78] hover:border-[#D4AF37]/60"
                    }`}
                  >
                    <span className="flex items-center gap-3">
                      <input
                        type="checkbox"
                        className="accent-[#D4AF37]"
                        checked={!!addons[g.id]}
                        onChange={(e) =>
                          setAddons((a) => ({ ...a, [g.id]: e.target.checked }))
                        }
                      />
                      {g.name}
                    </span>
                    <span className="font-semibold text-[#8A6D1F]">
                      {g.price}
                    </span>
                  </label>
                ))}
              </div>
            </div>

            <div>
              <p className="mb-3 text-[10px] uppercase tracking-[0.28em] text-[#9A7B24]">
                Finishing touches (optional)
              </p>
              <div className="grid gap-2 sm:grid-cols-3">
                {EXTRAS.map((x) => (
                  <label
                    key={x.id}
                    data-testid={`calc-extra-${x.id}`}
                    className={`flex cursor-pointer items-center justify-between gap-3 rounded-sm border px-4 py-3 text-sm transition-colors ${
                      extras[x.id]
                        ? "border-[#D4AF37] bg-[#D4AF37]/10 text-[#1B3A6B]"
                        : "border-[#D4AF37]/25 bg-white text-[#2F4A78] hover:border-[#D4AF37]/60"
                    }`}
                  >
                    <span className="flex items-center gap-3">
                      <input
                        type="checkbox"
                        className="accent-[#D4AF37]"
                        checked={!!extras[x.id]}
                        onChange={(e) =>
                          setExtras((a) => ({ ...a, [x.id]: e.target.checked }))
                        }
                      />
                      {x.name}
                    </span>
                    <span className="font-semibold text-[#8A6D1F]">
                      {x.price}
                    </span>
                  </label>
                ))}
              </div>
            </div>
          </div>

          <div
            className="flex flex-col rounded-sm border border-[#D4AF37]/40 bg-[#1B3A6B] p-8 text-white lg:col-span-2"
            data-testid="calc-summary"
          >
            <p className="flex items-center gap-2 font-label text-[10px] tracking-[0.3em] text-[#D4AF37]">
              <Calculator size={14} /> YOUR ESTIMATE
            </p>
            <p className="mt-6 font-display text-2xl">{cat.name}</p>
            <p className="mt-1 text-sm text-white/70">
              {tier.name} · {tier.serves}
            </p>
            {style && (
              <p className="mt-1 text-sm text-white/70">Style: {style}</p>
            )}
            <p className="mt-1 text-sm text-white/70">
              {flavor} · {filling}
            </p>

            <dl className="mt-8 space-y-2 border-t border-white/15 pt-6 text-sm">
              <div className="flex justify-between">
                <dt className="text-white/70">Cake</dt>
                <dd data-testid="calc-cake-price">{fmt(cakePrice)}</dd>
              </div>
              {[...chosenGifts, ...chosenExtras].map((g) => (
                <div key={g.id} className="flex justify-between">
                  <dt className="text-white/70">{g.name}</dt>
                  <dd>{g.price}</dd>
                </div>
              ))}
              <div className="flex justify-between border-t border-white/15 pt-3 text-lg">
                <dt className="font-display">Estimated total</dt>
                <dd
                  data-testid="calc-total"
                  className="font-display text-[#D4AF37]"
                >
                  {fmt(total)}
                </dd>
              </div>
              <div className="flex justify-between text-xs text-white/60">
                <dt>50% deposit to confirm</dt>
                <dd data-testid="calc-deposit">{fmt(total / 2)}</dd>
              </div>
            </dl>

            <p className="mt-6 text-[11px] leading-relaxed text-white/55">
              Guide price only — decoration detail, custom toppers and delivery
              can change the final quote.
              {addonTotal > 0 &&
                " Treat boxes need at least 3 days' notice; cakes need 2 weeks."}
            </p>

            <div className="mt-6">
              <RevolutDepositButton
                amount={fmt(total / 2)}
                dark
                testId="calc-revolut-button"
              />
            </div>

            <button
              data-testid="calc-enquire-button"
              onClick={() =>
                prefillEnquiry({
                  event_type: cat.eventType,
                  message: summary(),
                })
              }
              className="mt-auto flex items-center justify-center gap-2 rounded-full bg-[#D4AF37] px-6 py-3.5 text-sm font-semibold text-[#1B3A6B] transition-colors hover:bg-white"
            >
              Send this idea as an enquiry <ArrowRight size={16} />
            </button>
          </div>
        </motion.div>
      </div>
    </section>
  );
};
